"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useTransition } from "react";
import Link from "next/link";
import { MessageCircle, ThumbsUp, Pencil, Trash2, Loader2, Reply as ReplyIcon } from "lucide-react";
import { timeAgo } from "@/lib/format-utils";
import { createChapterComment, updateChapterComment, deleteChapterComment, toggleCommentLike, type ChapterCommentView } from "@/lib/actions/comment-actions";
const MAX_COMMENT_LENGTH = 2000;
interface ChapterCommentsProps {
  chapterId: string;
  initialComments: ChapterCommentView[];
  isAuthenticated: boolean;
  // The story owner sees delete controls on every comment (moderation).
  isStoryAuthor: boolean;
}

// Total comments including replies, for the section heading.
function countComments(comments: ChapterCommentView[]): number {
  return comments.reduce((sum, c) => sum + 1 + c.replies.length, 0);
}
export function ChapterComments(t0) {
  const $ = _c(27);
  const {
    chapterId,
    initialComments,
    isAuthenticated,
    isStoryAuthor
  } = t0;
  const [comments, setComments] = useState(initialComments);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState(null);
  const [isPosting, startPosting] = useTransition();
  let t1;
  if ($[0] !== chapterId || $[1] !== draft) {
    t1 = function handlePost() {
      const content = draft.trim();
      if (!content) {
        return;
      }
      setError(null);
      startPosting(async () => {
        ;
        try {
          const created = await createChapterComment(chapterId, content);
          setComments(prev => [created, ...prev]);
          setDraft("");
        } catch (t2) {
          const e = t2;
          setError(e instanceof Error ? e.message : "Failed to post comment");
        }
      });
    };
    $[0] = chapterId;
    $[1] = draft;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handlePost = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = function handleReplyCreated(reply) {
      setComments(prev_0 => prev_0.map(c => c.id === reply.parentId ? {
        ...c,
        replies: [...c.replies, reply]
      } : c));
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleReplyCreated = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = function handleDeleted(id) {
      setComments(prev_1 => prev_1.filter(c_0 => c_0.id !== id).map(c_1 => c_1.replies.some(r_0 => r_0.id === id) ? {
        ...c_1,
        replies: c_1.replies.filter(r => r.id !== id)
      } : c_1));
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleDeleted = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = function handleUpdated(id_0, content_0, updatedAt) {
      setComments(prev_2 => prev_2.map(c_2 => {
        if (c_2.id === id_0) {
          return {
            ...c_2,
            content: content_0,
            updatedAt,
            edited: true
          };
        }
        if (c_2.replies.some(r_2 => r_2.id === id_0)) {
          return {
            ...c_2,
            replies: c_2.replies.map(r_1 => r_1.id === id_0 ? {
              ...r_1,
              content: content_0,
              updatedAt,
              edited: true
            } : r_1)
          };
        }
        return c_2;
      }));
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleUpdated = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = function handleLikeChanged(id_1, liked, likeCount) {
      setComments(prev_3 => prev_3.map(c_3 => {
        if (c_3.id === id_1) {
          return {
            ...c_3,
            likedByMe: liked,
            likeCount
          };
        }
        if (c_3.replies.some(r_4 => r_4.id === id_1)) {
          return {
            ...c_3,
            replies: c_3.replies.map(r_3 => r_3.id === id_1 ? {
              ...r_3,
              likedByMe: liked,
              likeCount
            } : r_3)
          };
        }
        return c_3;
      }));
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleLikeChanged = t5;
  let t6;
  if ($[7] !== comments) {
    t6 = countComments(comments);
    $[7] = comments;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const total = t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <MessageCircle className="size-5 text-primary" />;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = total === 0 ? "Comments" : `${total} ${total === 1 ? "Comment" : "Comments"}`;
  let t9;
  if ($[10] !== t8) {
    t9 = <h3 className="flex items-center gap-2 text-lg font-bold font-heading mb-5">{t7}{t8}</h3>;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== draft || $[13] !== error || $[14] !== handlePost || $[15] !== isAuthenticated || $[16] !== isPosting) {
    t10 = isAuthenticated ? <div className="mb-8"><textarea value={draft} onChange={e_0 => setDraft(e_0.target.value.slice(0, MAX_COMMENT_LENGTH))} placeholder={"Share your thoughts on this chapter\u2026"} rows={3} aria-label="Write a comment" className="w-full resize-y rounded-xl border border-border bg-card px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30" /><div className="mt-2 flex items-center justify-between"><span className="text-xs text-muted-foreground">{draft.length}/{MAX_COMMENT_LENGTH}</span><button onClick={handlePost} disabled={isPosting || draft.trim().length === 0} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-primary/20 transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50">{isPosting && <Loader2 className="size-4 animate-spin" />}Post Comment</button></div>{error && <p className="mt-2 text-sm text-destructive" role="alert">{error}</p>}</div> : <Link href="/signup" aria-label="Sign up to post a comment" className="group mb-8 block"><div className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground transition-colors group-hover:border-primary">Share your thoughts on this chapter…</div><div className="mt-2 flex items-center justify-between"><span className="text-xs text-muted-foreground">Sign up to join the conversation</span><span className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-primary/20 transition-opacity group-hover:opacity-90">Post Comment</span></div></Link>;
    $[12] = draft;
    $[13] = error;
    $[14] = handlePost;
    $[15] = isAuthenticated;
    $[16] = isPosting;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== chapterId || $[19] !== comments || $[20] !== isAuthenticated || $[21] !== isStoryAuthor) {
    t11 = comments.length === 0 ? <p className="text-sm text-muted-foreground">No comments yet. Be the first to share your thoughts!</p> : <ul className="flex flex-col gap-5">{comments.map(comment => <CommentItem key={comment.id} comment={comment} chapterId={chapterId} isAuthenticated={isAuthenticated} isStoryAuthor={isStoryAuthor} onDeleted={handleDeleted} onUpdated={handleUpdated} onLikeChanged={handleLikeChanged} onReplyCreated={handleReplyCreated} />)}</ul>;
    $[18] = chapterId;
    $[19] = comments;
    $[20] = isAuthenticated;
    $[21] = isStoryAuthor;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t9) {
    t12 = <section id="comments" className="mt-12 scroll-mt-24">{t9}{t10}{t11}</section>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
}

// ─── Single comment ──────────────────────────────────────

interface CommentItemProps {
  comment: ChapterCommentView;
  chapterId: string;
  isAuthenticated: boolean;
  isStoryAuthor: boolean;
  // Single-level threading: replies CAN be replied to, but the new reply is
  // flattened onto the same root thread (server-side) rather than nested
  // deeper. When this item is itself a reply, its Reply composer is prefilled
  // with an @mention of the person being answered so the thread stays legible.
  isReply?: boolean;
  onDeleted: (id: string) => void;
  onUpdated: (id: string, content: string, updatedAt: Date | null) => void;
  onLikeChanged: (id: string, liked: boolean, likeCount: number) => void;
  onReplyCreated: (reply: ChapterCommentView) => void;
}
function CommentItem(t0) {
  const $ = _c(90);
  const {
    comment,
    chapterId,
    isAuthenticated,
    isStoryAuthor,
    isReply: t1,
    onDeleted,
    onUpdated,
    onLikeChanged,
    onReplyCreated
  } = t0;
  const isReply = t1 === undefined ? false : t1;
  const [isEditing, setIsEditing] = useState(false);
  const [editDraft, setEditDraft] = useState(comment.content);
  const [error, setError] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [replyDraft, setReplyDraft] = useState("");
  const [replyError, setReplyError] = useState(null);
  const [isPending, startTransition] = useTransition();
  const [isSendingReply, startReply] = useTransition();
  let t2;
  if ($[0] !== comment.authorName) {
    t2 = comment.authorName.trim().charAt(0).toUpperCase() || "?";
    $[0] = comment.authorName;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const initials = t2;
  const canDelete = comment.canDelete || isStoryAuthor;
  let t3;
  if ($[2] !== comment.id || $[3] !== comment.likeCount || $[4] !== comment.likedByMe || $[5] !== isAuthenticated || $[6] !== onLikeChanged) {
    t3 = function handleLike() {
      if (!isAuthenticated) {
        return;
      }
      const optimisticLiked = !comment.likedByMe;
      onLikeChanged(comment.id, optimisticLiked, comment.likeCount + (optimisticLiked ? 1 : -1));
      startTransition(async () => {
        try {
          const res = await toggleCommentLike(comment.id);
          onLikeChanged(comment.id, res.liked, res.likeCount);
        } catch {
          onLikeChanged(comment.id, comment.likedByMe, comment.likeCount);
        }
      });
    };
    $[2] = comment.id;
    $[3] = comment.likeCount;
    $[4] = comment.likedByMe;
    $[5] = isAuthenticated;
    $[6] = onLikeChanged;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleLike = t3;
  let t4;
  if ($[8] !== comment.id || $[9] !== editDraft || $[10] !== onUpdated) {
    t4 = function handleSaveEdit() {
      const content = editDraft.trim();
      if (!content) {
        return;
      }
      setError(null);
      startTransition(async () => {
        ;
        try {
          const res_0 = await updateChapterComment(comment.id, content);
          onUpdated(comment.id, res_0.content, res_0.updatedAt);
          setIsEditing(false);
        } catch (t5) {
          const e = t5;
          setError(e instanceof Error ? e.message : "Failed to save");
        }
      });
    };
    $[8] = comment.id;
    $[9] = editDraft;
    $[10] = onUpdated;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleSaveEdit = t4;
  let t5;
  if ($[12] !== comment.id || $[13] !== onDeleted) {
    t5 = function handleDelete() {
      startTransition(async () => {
        ;
        try {
          await deleteChapterComment(comment.id);
          onDeleted(comment.id);
        } catch (t6) {
          const e_0 = t6;
          setError(e_0 instanceof Error ? e_0.message : "Failed to delete");
          setConfirmingDelete(false);
        }
      });
    };
    $[12] = comment.id;
    $[13] = onDeleted;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const handleDelete = t5;
  let t6;
  if ($[15] !== chapterId || $[16] !== comment.id || $[17] !== onReplyCreated || $[18] !== replyDraft) {
    t6 = function handleSendReply() {
      const content_0 = replyDraft.trim();
      if (!content_0) {
        return;
      }
      setReplyError(null);
      startReply(async () => {
        ;
        try {
          const created = await createChapterComment(chapterId, content_0, comment.id);
          onReplyCreated(created);
          setReplyDraft("");
          setIsReplying(false);
        } catch (t7) {
          const e_1 = t7;
          setReplyError(e_1 instanceof Error ? e_1.message : "Failed to post reply");
        }
      });
    };
    $[15] = chapterId;
    $[16] = comment.id;
    $[17] = onReplyCreated;
    $[18] = replyDraft;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const handleSendReply = t6;
  let t7;
  if ($[20] !== comment.authorAvatar || $[21] !== initials || $[22] !== isReply) {
    t7 = comment.authorAvatar ? <img src={comment.authorAvatar} alt="" className={`shrink-0 rounded-full object-cover ${isReply ? "size-7" : "size-9"}`} /> : <div className={`flex shrink-0 items-center justify-center rounded-full bg-primary/15 font-semibold text-primary ${isReply ? "size-7 text-xs" : "size-9 text-sm"}`}>{initials}</div>;
    $[20] = comment.authorAvatar;
    $[21] = initials;
    $[22] = isReply;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== comment.authorName || $[25] !== comment.authorSlug) {
    t8 = comment.authorSlug ? <Link href={`/${comment.authorSlug}`} className="text-sm font-semibold text-foreground hover:text-primary hover:underline">{comment.authorName}</Link> : <span className="text-sm font-semibold text-foreground">{comment.authorName}</span>;
    $[24] = comment.authorName;
    $[25] = comment.authorSlug;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== comment.isStoryAuthor) {
    t9 = comment.isStoryAuthor && <span className="inline-flex items-center rounded-full bg-[color-mix(in_srgb,var(--primary)_20%,transparent)] px-2 py-0.5 text-[0.7rem] font-semibold uppercase tracking-wide text-primary">Author</span>;
    $[27] = comment.isStoryAuthor;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== comment.createdAt) {
    t10 = timeAgo(comment.createdAt);
    $[29] = comment.createdAt;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const t11 = comment.edited && " \xB7 edited";
  let t12;
  if ($[31] !== t10 || $[32] !== t11) {
    t12 = <span className="text-xs text-muted-foreground">{t10}{t11}</span>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== t12 || $[35] !== t8 || $[36] !== t9) {
    t13 = <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">{t8}{t9}{t12}</div>;
    $[34] = t12;
    $[35] = t8;
    $[36] = t9;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== comment.content || $[39] !== editDraft || $[40] !== handleSaveEdit || $[41] !== isEditing || $[42] !== isPending) {
    t14 = isEditing ? <div className="mt-2"><textarea value={editDraft} onChange={e_2 => setEditDraft(e_2.target.value.slice(0, MAX_COMMENT_LENGTH))} rows={3} aria-label="Edit comment" className="w-full resize-y rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30" /><div className="mt-2 flex gap-2"><button onClick={handleSaveEdit} disabled={isPending || editDraft.trim().length === 0} className="rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Save</button><button onClick={() => {
          setIsEditing(false);
          setEditDraft(comment.content);
          setError(null);
        }} className="rounded-full px-4 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground">Cancel</button></div></div> : <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700 dark:text-slate-300">{comment.content}</p>;
    $[38] = comment.content;
    $[39] = editDraft;
    $[40] = handleSaveEdit;
    $[41] = isEditing;
    $[42] = isPending;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== error) {
    t15 = error && <p className="mt-1 text-xs text-destructive" role="alert">{error}</p>;
    $[44] = error;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== canDelete || $[47] !== comment.authorName || $[48] !== comment.canEdit || $[49] !== comment.id || $[50] !== comment.likeCount || $[51] !== comment.likedByMe || $[52] !== confirmingDelete || $[53] !== handleDelete || $[54] !== handleLike || $[55] !== isAuthenticated || $[56] !== isEditing || $[57] !== isPending || $[58] !== isReply || $[59] !== isReplying || $[60] !== replyDraft) {
    t16 = !isEditing && <div className="mt-2 flex items-center gap-4 text-xs"><button onClick={handleLike} disabled={!isAuthenticated} aria-pressed={comment.likedByMe} aria-label={comment.likedByMe ? "Unlike comment" : "Like comment"} className={`inline-flex items-center gap-1 transition-colors disabled:cursor-not-allowed ${comment.likedByMe ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}><ThumbsUp className={`size-4 ${comment.likedByMe ? "fill-current" : ""}`} />{comment.likeCount > 0 && <span>{comment.likeCount}</span>}</button>{isAuthenticated && <button onClick={() => {
        if (!isReplying && isReply && replyDraft.trim() === "") {
          setReplyDraft(`@${comment.authorName} `);
        }
        setIsReplying(_temp);
      }} aria-expanded={isReplying} aria-controls={`reply-composer-${comment.id}`} className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"><ReplyIcon className="size-3.5" />Reply</button>}{comment.canEdit && <button onClick={() => setIsEditing(true)} className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"><Pencil className="size-3.5" />Edit</button>}{canDelete && (confirmingDelete ? <span className="inline-flex items-center gap-2"><span className="text-muted-foreground">Delete?</span><button onClick={handleDelete} disabled={isPending} className="font-semibold text-destructive hover:underline disabled:opacity-50">Yes</button><button onClick={() => setConfirmingDelete(false)} disabled={isPending} className="text-muted-foreground hover:text-foreground">Cancel</button></span> : <button onClick={() => setConfirmingDelete(true)} disabled={isPending} className="inline-flex items-center gap-1 text-muted-foreground hover:text-destructive disabled:opacity-50"><Trash2 className="size-3.5" />Delete</button>)}</div>;
    $[46] = canDelete;
    $[47] = comment.authorName;
    $[48] = comment.canEdit;
    $[49] = comment.id;
    $[50] = comment.likeCount;
    $[51] = comment.likedByMe;
    $[52] = confirmingDelete;
    $[53] = handleDelete;
    $[54] = handleLike;
    $[55] = isAuthenticated;
    $[56] = isEditing;
    $[57] = isPending;
    $[58] = isReply;
    $[59] = isReplying;
    $[60] = replyDraft;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  let t17;
  if ($[62] !== comment.authorName || $[63] !== comment.id || $[64] !== handleSendReply || $[65] !== isReplying || $[66] !== isSendingReply || $[67] !== replyDraft || $[68] !== replyError) {
    t17 = isReplying && <div className="mt-3" id={`reply-composer-${comment.id}`}><textarea value={replyDraft} onChange={e_3 => setReplyDraft(e_3.target.value.slice(0, MAX_COMMENT_LENGTH))} placeholder={`Reply to ${comment.authorName}…`} rows={2} aria-label="Write a reply" className="w-full resize-y rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30" /><div className="mt-2 flex items-center justify-between"><span className="text-xs text-muted-foreground">{replyDraft.length}/{MAX_COMMENT_LENGTH}</span><div className="flex gap-2"><button onClick={handleSendReply} disabled={isSendingReply || replyDraft.trim().length === 0} className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">{isSendingReply && <Loader2 className="size-3.5 animate-spin" />}Post Reply</button><button onClick={() => {
            setIsReplying(false);
            setReplyDraft("");
            setReplyError(null);
          }} className="rounded-full px-4 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground">Cancel</button></div></div>{replyError && <p className="mt-1 text-xs text-destructive" role="alert">{replyError}</p>}</div>;
    $[62] = comment.authorName;
    $[63] = comment.id;
    $[64] = handleSendReply;
    $[65] = isReplying;
    $[66] = isSendingReply;
    $[67] = replyDraft;
    $[68] = replyError;
    $[69] = t17;
  } else {
    t17 = $[69];
  }
  let t18;
  if ($[70] !== chapterId || $[71] !== comment.replies || $[72] !== isAuthenticated || $[73] !== isReply || $[74] !== isStoryAuthor || $[75] !== onDeleted || $[76] !== onLikeChanged || $[77] !== onReplyCreated || $[78] !== onUpdated) {
    t18 = !isReply && comment.replies.length > 0 && <ul className="mt-4 flex flex-col gap-4 border-l border-border pl-3 sm:pl-4">{comment.replies.map(reply => <CommentItem key={reply.id} comment={reply} chapterId={chapterId} isAuthenticated={isAuthenticated} isStoryAuthor={isStoryAuthor} isReply={true} onDeleted={onDeleted} onUpdated={onUpdated} onLikeChanged={onLikeChanged} onReplyCreated={onReplyCreated} />)}</ul>;
    $[70] = chapterId;
    $[71] = comment.replies;
    $[72] = isAuthenticated;
    $[73] = isReply;
    $[74] = isStoryAuthor;
    $[75] = onDeleted;
    $[76] = onLikeChanged;
    $[77] = onReplyCreated;
    $[78] = onUpdated;
    $[79] = t18;
  } else {
    t18 = $[79];
  }
  let t19;
  if ($[80] !== t13 || $[81] !== t14 || $[82] !== t15 || $[83] !== t16 || $[84] !== t17 || $[85] !== t18) {
    t19 = <div className="min-w-0 flex-1">{t13}{t14}{t15}{t16}{t17}{t18}</div>;
    $[80] = t13;
    $[81] = t14;
    $[82] = t15;
    $[83] = t16;
    $[84] = t17;
    $[85] = t18;
    $[86] = t19;
  } else {
    t19 = $[86];
  }
  let t20;
  if ($[87] !== t19 || $[88] !== t7) {
    t20 = <li className="flex gap-3">{t7}{t19}</li>;
    $[87] = t19;
    $[88] = t7;
    $[89] = t20;
  } else {
    t20 = $[89];
  }
  return t20;
}
function _temp(v) {
  return !v;
}